/**
 * Enrollment number → source system mapping.
 *
 * To add a new prefix: Platform Config → Sources → open source → Enrollment prefix rules.
 * Code defaults below apply only when the DB has no active rules (first boot).
 */

export type LinkedEntitySource = 'ERP' | 'GARAGES' | 'FLOWW';

export interface EnrollmentSourceRule {
  source: LinkedEntitySource;
  /** Shown in UI dropdowns and hints. */
  label: string;
  /** Longest matching prefix wins (case-insensitive). */
  prefixes: string[];
  description?: string;
}

export const DEFAULT_ENROLLMENT_SOURCE_RULES: EnrollmentSourceRule[] = [
  {
    source: 'ERP',
    label: 'ERP / Parenthub',
    prefixes: ['21KE', '21KS'],
    description: '21K School ERP and Parenthub enrollments',
  },
  {
    source: 'GARAGES',
    label: 'Garages',
    prefixes: ['21LG'],
    description: 'Garages learner enrollments',
  },
  {
    source: 'FLOWW',
    label: 'FLOWW',
    prefixes: ['21LF', '21LW'],
    description: 'FLOWW learner enrollments',
  },
];

function isEnrollmentSourceRule(value: unknown): value is EnrollmentSourceRule {
  if (!value || typeof value !== 'object') return false;
  const r = value as EnrollmentSourceRule;
  return (
    (r.source === 'ERP' || r.source === 'GARAGES' || r.source === 'FLOWW') &&
    typeof r.label === 'string' &&
    r.label.length > 0 &&
    Array.isArray(r.prefixes) &&
    r.prefixes.length > 0 &&
    r.prefixes.every((p) => typeof p === 'string' && p.length > 0)
  );
}

/** Parse ENROLLMENT_SOURCE_RULES_JSON env override; falls back to defaults. */
export function parseEnrollmentSourceRules(json?: string): EnrollmentSourceRule[] {
  if (!json?.trim()) return DEFAULT_ENROLLMENT_SOURCE_RULES;
  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return DEFAULT_ENROLLMENT_SOURCE_RULES;
    const rules = parsed.filter(isEnrollmentSourceRule);
    return rules.length > 0 ? rules : DEFAULT_ENROLLMENT_SOURCE_RULES;
  } catch {
    return DEFAULT_ENROLLMENT_SOURCE_RULES;
  }
}

/** Longest-prefix match — advisory only (UI hints / mismatch warnings). */
export function inferEnrollmentSourceFromRules(
  enrollmentNumber: string,
  rules: EnrollmentSourceRule[] = DEFAULT_ENROLLMENT_SOURCE_RULES,
): LinkedEntitySource | null {
  const u = enrollmentNumber.trim().toUpperCase();
  if (!u) return null;

  let best: { source: LinkedEntitySource; len: number } | null = null;
  for (const rule of rules) {
    for (const prefix of rule.prefixes) {
      const p = prefix.trim().toUpperCase();
      if (p && u.startsWith(p) && (!best || p.length > best.len)) {
        best = { source: rule.source, len: p.length };
      }
    }
  }
  return best?.source ?? null;
}

export function formatEnrollmentSourceHint(
  rules: EnrollmentSourceRule[] = DEFAULT_ENROLLMENT_SOURCE_RULES,
): string {
  return rules
    .map((r) => `${r.label}: ${r.prefixes.join(', ')}…`)
    .join(' · ');
}

/** @deprecated Use inferEnrollmentSourceFromRules — kept for compatibility. */
export function inferEnrollmentSource(enrollmentNumber: string): LinkedEntitySource | null {
  return inferEnrollmentSourceFromRules(enrollmentNumber);
}

/** @deprecated Use formatEnrollmentSourceHint — kept for compatibility. */
export const ENROLLMENT_NUMBER_FORMAT_HINT = formatEnrollmentSourceHint();
